import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { LogOut, Package, CreditCard, FileText, RefreshCw, Clock, CheckCircle, AlertCircle, User, Download, XCircle, Truck, Receipt, ClipboardCheck, Calendar, Sparkles, Edit, Save, X, MapPin, Building, Lock, Eye, EyeOff } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import axios from 'axios';
import { clearSessionToken } from '../lib/auth';
import { toast } from 'sonner';
import ReturnRequestModal from './ReturnRequestModal';
import InvoicesModal from './InvoicesModal';
import { PackName } from './ui/PackName';

const LOGO_WHITE = process.env.REACT_APP_LOGO_URL;
const API = `${window.location.origin}/api`;

const ClientDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState(location.state?.user || null);
  const [reservations, setReservations] = useState([]);
  const [returnRequests, setReturnRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(location.state?.user ? true : null);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [showInvoicesModal, setShowInvoicesModal] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [cancellingId, setCancellingId] = useState(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(null);
  const [passwordForm, setPasswordForm] = useState({ current: '', new: '', confirm: '' });
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [showPasswords, setShowPasswords] = useState(false);
  
  // Profile states
  const [activeTab, setActiveTab] = useState('locations'); // 'locations', 'quotes', 'invoices', 'profile'
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [quotes, setQuotes] = useState([]);
  const [quotesLoading, setQuotesLoading] = useState(false);
  const [clientInvoices, setClientInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [profile, setProfile] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    company: '',
    delivery_address: '',
    delivery_city: '',
    delivery_postal_code: '',
    billing_address: '',
    billing_city: '',
    billing_postal_code: '',
    billing_same_as_delivery: true
  });

  // Check for return success/cancelled from URL params
  useEffect(() => {
    const confirmReturnRequest = async () => {
      const reservationId = searchParams.get('reservation_id');
      const sessionId = localStorage.getItem('return_session_id');
      
      if (searchParams.get('return_success') === 'true' && reservationId && sessionId) {
        try {
          // Confirm the return request with the backend
          await axios.post(`${API}/returns/confirm`, {
            session_id: sessionId,
            reservation_id: reservationId
          }, { withCredentials: true });
          
          // Clear stored session ID
          localStorage.removeItem('return_session_id');
          
          toast.success('🎉 Demande de résiliation enregistrée ! Vous recevrez le bordereau de retour par email.');
        } catch (error) {
          console.error('Error confirming return:', error);
          if (error.response?.data?.status === 'already_processed') {
            toast.info('Cette demande a déjà été traitée.');
          } else {
            toast.error('Erreur lors de la confirmation. Contactez le support.');
          }
        }
      } else if (searchParams.get('return_success') === 'true') {
        toast.success('🎉 Demande de résiliation enregistrée !');
      } else if (searchParams.get('return_cancelled') === 'true') {
        toast.info('Paiement annulé. Vous pouvez réessayer quand vous le souhaitez.');
      }
    };
    
    confirmReturnRequest();
  }, [searchParams]);

  useEffect(() => {
    // Skip if user passed from AuthCallback
    if (location.state?.user) {
      setIsAuthenticated(true);
      setUser(location.state.user);
      fetchReservations(location.state.user.email);
      return;
    }

    // Check auth via /auth/me
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(response.data);
        setIsAuthenticated(true);
        
        // If admin, redirect to admin panel
        if (response.data.is_admin) {
          navigate('/admin', { state: { user: response.data } });
          return;
        }
        
        fetchReservations(response.data.email);
      } catch (error) {
        console.error('Auth check failed:', error);
        setIsAuthenticated(false);
        navigate('/login');
      }
    };

    checkAuth();
  }, [navigate, location.state]);

  const fetchReservations = async (email) => {
    try {
      const response = await axios.get(`${API}/reservations/user/${encodeURIComponent(email)}`, { withCredentials: true });
      setReservations(response.data);
      // Also fetch return requests
      fetchReturnRequests(email);
    } catch (error) {
      console.error('Failed to fetch reservations:', error);
      toast.error('Erreur lors du chargement des réservations');
    } finally {
      setLoading(false);
    }
  };

  const fetchReturnRequests = async (email) => {
    try {
      const response = await axios.get(`${API}/returns/user/${encodeURIComponent(email)}`, { withCredentials: true });
      setReturnRequests(response.data);
    } catch (error) {
      console.error('Failed to fetch return requests:', error);
    }
  };

  const downloadContract = (reservationId) => {
    window.open(`${API}/contracts/${reservationId}/pdf`, '_blank');
  };

  const fetchQuotes = async () => {
    setQuotesLoading(true);
    try {
      const response = await axios.get(`${API}/client/quotes`, { withCredentials: true });
      setQuotes(response.data);
    } catch (error) {
      console.error('Failed to fetch quotes:', error);
    } finally {
      setQuotesLoading(false);
    }
  };

  const fetchClientInvoices = async () => {
    setInvoicesLoading(true);
    try {
      const response = await axios.get(`${API}/client/invoices`, { withCredentials: true });
      setClientInvoices(response.data);
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
    } finally {
      setInvoicesLoading(false);
    }
  };

  const handleAcceptQuote = async (quoteId) => {
    try {
      await axios.post(`${API}/client/quotes/${quoteId}/accept`, {}, { withCredentials: true });
      toast.success('Devis accepté !');
      fetchQuotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'acceptation');
    }
  };

  const handleRejectQuote = async (quoteId) => {
    try {
      await axios.post(`${API}/client/quotes/${quoteId}/reject`, {}, { withCredentials: true });
      toast.success('Devis refusé');
      fetchQuotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const downloadQuotePdf = (quoteId) => {
    window.open(`${API}/client/quotes/${quoteId}/pdf`, '_blank');
  };

  const openCustomerPortal = async (customerId) => {
    if (!customerId) {
      toast.error('Portail de facturation non disponible');
      return;
    }
    try {
      const response = await axios.post(`${API}/stripe/customer-portal`, {
        customer_id: customerId,
        return_url: window.location.href
      }, { withCredentials: true });
      
      if (response.data.portal_url) {
        window.location.href = response.data.portal_url;
      }
    } catch (error) {
      console.error('Portal error:', error);
      toast.error('Erreur lors de l\'ouverture du portail de facturation');
    }
  };

  const openReturnModal = (reservation) => {
    setSelectedReservation(reservation);
    setShowReturnModal(true);
  };

  const openInvoicesModal = (customerId) => {
    setSelectedCustomerId(customerId);
    setShowInvoicesModal(true);
  };

  const handleCancelOrder = async (reservationId) => {
    setCancellingId(reservationId);
    try {
      await axios.post(`${API}/reservations/${reservationId}/cancel`, {}, { withCredentials: true });
      toast.success('Commande annulée — remboursement initié');
      setShowCancelConfirm(null);
      if (user?.email) fetchReservations(user.email);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'annulation");
    } finally {
      setCancellingId(null);
    }
  };

  const handleChangePassword = async () => {
    if (!passwordForm.current) return toast.error('Veuillez saisir votre mot de passe actuel');
    if (!passwordForm.new) return toast.error('Veuillez saisir le nouveau mot de passe');
    if (passwordForm.new.length < 8) return toast.error('Le nouveau mot de passe doit contenir au moins 8 caractères');
    if (passwordForm.new !== passwordForm.confirm) return toast.error('Les mots de passe ne correspondent pas');

    setPasswordSaving(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordForm.current,
        new_password: passwordForm.new
      }, { withCredentials: true });
      toast.success('Mot de passe modifié avec succès');
      setPasswordForm({ current: '', new: '', confirm: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du changement de mot de passe');
    } finally {
      setPasswordSaving(false);
    }
  };

  const getReturnRequestForReservation = (reservationId) => {
    return returnRequests.find(r => r.reservation_id === reservationId);
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      clearSessionToken();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      clearSessionToken();
      navigate('/login');
    }
  };

  const fetchProfile = async () => {
    setProfileLoading(true);
    try {
      const response = await axios.get(`${API}/auth/profile`, { withCredentials: true });
      setProfile({
        first_name: response.data.first_name || '',
        last_name: response.data.last_name || '',
        phone: response.data.phone || '',
        company: response.data.company || '',
        delivery_address: response.data.delivery_address || '',
        delivery_city: response.data.delivery_city || '',
        delivery_postal_code: response.data.delivery_postal_code || '',
        billing_address: response.data.billing_address || '',
        billing_city: response.data.billing_city || '',
        billing_postal_code: response.data.billing_postal_code || '',
        billing_same_as_delivery: response.data.billing_same_as_delivery ?? true
      });
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    } finally {
      setProfileLoading(false);
    }
  };

  const handleProfileChange = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  const saveProfile = async () => {
    setProfileSaving(true);
    try {
      await axios.put(`${API}/auth/profile`, profile, { withCredentials: true });
      toast.success('Profil mis à jour avec succès');
      // Update the user name in header if changed
      if (profile.first_name || profile.last_name) {
        setUser(prev => ({
          ...prev,
          name: `${profile.first_name} ${profile.last_name}`.trim() || prev.name
        }));
      }
    } catch (error) {
      console.error('Failed to save profile:', error);
      toast.error('Erreur lors de la sauvegarde du profil');
    } finally {
      setProfileSaving(false);
    }
  };

  // Fetch profile when tab changes to profile
  useEffect(() => {
    if (activeTab === 'profile' && isAuthenticated) {
      fetchProfile();
    } else if (activeTab === 'quotes' && isAuthenticated) {
      fetchQuotes();
    } else if (activeTab === 'invoices' && isAuthenticated) {
      fetchClientInvoices();
    }
  }, [activeTab, isAuthenticated]);

  const getStatusBadge = (status, returnRequest) => {
    // If there's a return request, show termination status
    if (returnRequest) {
      const returnBadges = {
        pending: { icon: Clock, text: 'Résiliation en cours', color: 'bg-orange-500/20 text-orange-400' },
        label_sent: { icon: Truck, text: 'Bordereau envoyé', color: 'bg-blue-500/20 text-blue-400' },
        completed: { icon: XCircle, text: 'Résilié', color: 'bg-slate-500/20 text-slate-400' }
      };
      const badge = returnBadges[returnRequest.status] || returnBadges.pending;
      const Icon = badge.icon;
      return (
        <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm ${badge.color}`}>
          <Icon className="w-4 h-4" />
          {badge.text}
        </span>
      );
    }
    
    const badges = {
      confirmed: { icon: CheckCircle, text: 'Actif', color: 'bg-green-500/20 text-green-400' },
      pending: { icon: Clock, text: 'En attente', color: 'bg-yellow-500/20 text-yellow-400' },
      pending_validation: { icon: Clock, text: 'En validation', color: 'bg-yellow-500/20 text-yellow-400' },
      cancelled: { icon: AlertCircle, text: 'Annulé', color: 'bg-red-500/20 text-red-400' },
      terminated: { icon: XCircle, text: 'Terminé', color: 'bg-slate-500/20 text-slate-400' }
    };
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm ${badge.color}`}>
        <Icon className="w-4 h-4" />
        {badge.text}
      </span>
    );
  };

  if (isAuthenticated === null || loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#DFFF00] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <header className="bg-black border-b border-slate-800">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img 
                src={LOGO_WHITE} 
                alt="Phocea Rent" 
                className="h-10 w-auto cursor-pointer"
                onClick={() => navigate('/')}
              />
              <div className="hidden sm:block text-slate-400 text-sm">Espace Client</div>
            </div>
            
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/')}
                className="text-slate-400 hover:text-[#DFFF00] hidden sm:flex items-center gap-1.5"
                data-testid="back-to-site-btn"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                <span className="text-sm">Voir le site</span>
              </Button>
              {user && (
                <div className="flex items-center gap-3">
                  {user.picture ? (
                    <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-[#DFFF00] flex items-center justify-center">
                      <User className="w-4 h-4 text-black" />
                    </div>
                  )}
                  <span className="hidden sm:block text-white text-sm">{user.name}</span>
                </div>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-slate-400 hover:text-white"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Tabs */}
        <div className="flex gap-3 mb-8 flex-wrap">
          <button
            onClick={() => setActiveTab('locations')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'locations'
                ? 'bg-[#DFFF00] text-black'
                : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
            }`}
          >
            <Package className="w-4 h-4" />
            Mes locations
          </button>
          <button
            onClick={() => setActiveTab('quotes')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'quotes'
                ? 'bg-[#DFFF00] text-black'
                : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
            }`}
            data-testid="tab-quotes"
          >
            <FileText className="w-4 h-4" />
            Mes devis
            {quotes.length > 0 && (
              <span className="ml-1 px-1.5 py-0.5 text-xs rounded-full bg-slate-700 text-slate-300">{quotes.length}</span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('invoices')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'invoices'
                ? 'bg-[#DFFF00] text-black'
                : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
            }`}
            data-testid="tab-invoices"
          >
            <Receipt className="w-4 h-4" />
            Mes factures
          </button>
          <button
            onClick={() => setActiveTab('profile')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'profile'
                ? 'bg-[#DFFF00] text-black'
                : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
            }`}
          >
            <User className="w-4 h-4" />
            Mon profil
          </button>
        </div>

        {/* Quotes Tab */}
        {activeTab === 'quotes' && (
          <div className="bg-slate-900 rounded-2xl border border-slate-800 overflow-hidden" data-testid="quotes-section">
            <div className="bg-slate-800/50 px-6 py-4 flex items-center gap-3">
              <FileText className="w-5 h-5 text-[#DFFF00]" />
              <span className="font-semibold text-white">Mes devis</span>
            </div>
            {quotesLoading ? (
              <div className="p-8 text-center text-slate-400">Chargement...</div>
            ) : quotes.length === 0 ? (
              <div className="p-8 text-center text-slate-500">Aucun devis pour le moment.</div>
            ) : (
              <div className="divide-y divide-slate-800">
                {quotes.map((quote) => {
                  const statusMap = {
                    draft: { label: 'Brouillon', cls: 'bg-slate-700 text-slate-300' },
                    sent: { label: 'En attente', cls: 'bg-amber-500/20 text-amber-400' },
                    accepted: { label: 'Accepté', cls: 'bg-green-500/20 text-green-400' },
                    rejected: { label: 'Refusé', cls: 'bg-red-500/20 text-red-400' },
                    converted: { label: 'Converti', cls: 'bg-blue-500/20 text-blue-400' },
                    expired: { label: 'Expiré', cls: 'bg-slate-600 text-slate-400' },
                  };
                  const st = statusMap[quote.status] || statusMap.draft;
                  return (
                    <div key={quote.id} className="px-6 py-4 flex items-center justify-between gap-4" data-testid={`quote-${quote.id}`}>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <span className="text-sm font-semibold text-white">{quote.quote_number}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${st.cls}`}>{st.label}</span>
                        </div>
                        <p className="text-sm text-slate-400">{quote.pack_name} — {quote.duration_months} mois</p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          Créé le {new Date(quote.created_at).toLocaleDateString('fr-FR')}
                          {quote.expiry_date && ` · Valide jusqu'au ${new Date(quote.expiry_date).toLocaleDateString('fr-FR')}`}
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-base font-bold text-white">{Number(quote.total_ttc || 0).toFixed(2)} €</p>
                        <p className="text-xs text-slate-500">TTC</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          onClick={() => downloadQuotePdf(quote.id)}
                          className="p-2 rounded-lg bg-slate-800 text-slate-300 hover:bg-slate-700 transition-colors"
                          title="Télécharger PDF"
                          data-testid={`download-quote-${quote.id}`}
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        {quote.status === 'sent' && (
                          <>
                            <button
                              onClick={() => handleAcceptQuote(quote.id)}
                              className="px-3 py-1.5 rounded-lg bg-green-600/20 text-green-400 text-sm font-medium hover:bg-green-600/30 transition-colors"
                              data-testid={`accept-quote-${quote.id}`}
                            >
                              Accepter
                            </button>
                            <button
                              onClick={() => handleRejectQuote(quote.id)}
                              className="px-3 py-1.5 rounded-lg bg-red-600/20 text-red-400 text-sm font-medium hover:bg-red-600/30 transition-colors"
                              data-testid={`reject-quote-${quote.id}`}
                            >
                              Refuser
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Invoices Tab */}
        {activeTab === 'invoices' && (
          <div className="bg-slate-900 rounded-2xl border border-slate-800 overflow-hidden" data-testid="invoices-section">
            <div className="bg-slate-800/50 px-6 py-4 flex items-center gap-3">
              <Receipt className="w-5 h-5 text-[#DFFF00]" />
              <span className="font-semibold text-white">Mes factures</span>
            </div>
            {invoicesLoading ? (
              <div className="p-8 text-center text-slate-400">Chargement...</div>
            ) : clientInvoices.length === 0 ? (
              <div className="p-8 text-center text-slate-500">Aucune facture pour le moment.</div>
            ) : (
              <div className="divide-y divide-slate-800">
                {clientInvoices.map((inv) => {
                  const statusMap = {
                    paid: { label: 'Payée', cls: 'bg-green-500/20 text-green-400' },
                    open: { label: 'En attente', cls: 'bg-amber-500/20 text-amber-400' },
                    draft: { label: 'Brouillon', cls: 'bg-slate-700 text-slate-300' },
                    void: { label: 'Annulée', cls: 'bg-red-500/20 text-red-400' },
                    uncollectible: { label: 'Impayée', cls: 'bg-red-500/20 text-red-400' },
                  };
                  const st = statusMap[inv.status] || statusMap.draft;
                  return (
                    <div key={inv.id} className="px-6 py-4 flex items-center justify-between gap-4" data-testid={`invoice-${inv.id}`}>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <span className="text-sm font-semibold text-white">{inv.number || 'Facture'}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${st.cls}`}>{st.label}</span>
                          {inv.type === 'stripe' && (
                            <span className="text-xs px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-400">Stripe</span>
                          )}
                        </div>
                        <p className="text-sm text-slate-400">{inv.description}</p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {inv.date ? new Date(inv.date).toLocaleDateString('fr-FR') : ''}
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-base font-bold text-white">{Number(inv.amount || 0).toFixed(2)} €</p>
                      </div>
                      <div className="shrink-0">
                        {inv.pdf_url && (
                          <a
                            href={inv.pdf_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 rounded-lg bg-slate-800 text-slate-300 hover:bg-slate-700 transition-colors inline-flex"
                            title="Télécharger PDF"
                            data-testid={`download-invoice-${inv.id}`}
                          >
                            <Download className="w-4 h-4" />
                          </a>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="bg-slate-900 rounded-2xl border border-slate-800 overflow-hidden">
            <div className="bg-slate-800/50 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <User className="w-5 h-5 text-[#DFFF00]" />
                <span className="font-semibold text-white">Mes informations personnelles</span>
              </div>
            </div>

            {profileLoading ? (
              <div className="p-12 flex items-center justify-center">
                <div className="animate-spin w-8 h-8 border-2 border-[#DFFF00] border-t-transparent rounded-full"></div>
              </div>
            ) : (
              <div className="p-6">
                {/* Identité */}
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <User className="w-5 h-5 text-[#DFFF00]" />
                    Identité
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-400">Prénom</Label>
                      <Input
                        value={profile.first_name}
                        onChange={(e) => handleProfileChange('first_name', e.target.value)}
                        placeholder="Votre prénom"
                        className="mt-1 bg-slate-800 border-slate-700 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-400">Nom</Label>
                      <Input
                        value={profile.last_name}
                        onChange={(e) => handleProfileChange('last_name', e.target.value)}
                        placeholder="Votre nom"
                        className="mt-1 bg-slate-800 border-slate-700 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-400">Téléphone</Label>
                      <Input
                        value={profile.phone}
                        onChange={(e) => handleProfileChange('phone', e.target.value)}
                        placeholder="06 12 34 56 78"
                        className="mt-1 bg-slate-800 border-slate-700 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-400">Société</Label>
                      <Input
                        value={profile.company}
                        onChange={(e) => handleProfileChange('company', e.target.value)}
                        placeholder="Nom de votre société"
                        className="mt-1 bg-slate-800 border-slate-700 text-white"
                      />
                    </div>
                  </div>
                </div>

                {/* Adresse de livraison */}
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <Truck className="w-5 h-5 text-[#DFFF00]" />
                    Adresse de livraison
                  </h3>
                  <div className="grid md:grid-cols-1 gap-4">
                    <div>
                      <Label className="text-slate-400">Adresse</Label>
                      <Input
                        value={profile.delivery_address}
                        onChange={(e) => handleProfileChange('delivery_address', e.target.value)}
                        placeholder="123 Rue Exemple"
                        className="mt-1 bg-slate-800 border-slate-700 text-white"
                      />
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-400">Ville</Label>
                        <Input
                          value={profile.delivery_city}
                          onChange={(e) => handleProfileChange('delivery_city', e.target.value)}
                          placeholder="Marseille"
                          className="mt-1 bg-slate-800 border-slate-700 text-white"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-400">Code postal</Label>
                        <Input
                          value={profile.delivery_postal_code}
                          onChange={(e) => handleProfileChange('delivery_postal_code', e.target.value)}
                          placeholder="13000"
                          className="mt-1 bg-slate-800 border-slate-700 text-white"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Adresse de facturation */}
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <Building className="w-5 h-5 text-[#DFFF00]" />
                    Adresse de facturation
                  </h3>
                  
                  <div className="flex items-center gap-3 mb-4">
                    <Checkbox
                      id="billing-same"
                      checked={profile.billing_same_as_delivery}
                      onCheckedChange={(checked) => handleProfileChange('billing_same_as_delivery', checked)}
                      className="data-[state=checked]:bg-[#DFFF00] data-[state=checked]:border-[#DFFF00]"
                    />
                    <label htmlFor="billing-same" className="text-slate-300 cursor-pointer">
                      Identique à l'adresse de livraison
                    </label>
                  </div>

                  {!profile.billing_same_as_delivery && (
                    <div className="grid md:grid-cols-1 gap-4">
                      <div>
                        <Label className="text-slate-400">Adresse</Label>
                        <Input
                          value={profile.billing_address}
                          onChange={(e) => handleProfileChange('billing_address', e.target.value)}
                          placeholder="123 Rue Exemple"
                          className="mt-1 bg-slate-800 border-slate-700 text-white"
                        />
                      </div>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label className="text-slate-400">Ville</Label>
                          <Input
                            value={profile.billing_city}
                            onChange={(e) => handleProfileChange('billing_city', e.target.value)}
                            placeholder="Marseille"
                            className="mt-1 bg-slate-800 border-slate-700 text-white"
                          />
                        </div>
                        <div>
                          <Label className="text-slate-400">Code postal</Label>
                          <Input
                            value={profile.billing_postal_code}
                            onChange={(e) => handleProfileChange('billing_postal_code', e.target.value)}
                            placeholder="13000"
                            className="mt-1 bg-slate-800 border-slate-700 text-white"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Modifier mot de passe */}
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <Lock className="w-5 h-5 text-[#DFFF00]" />
                    Modifier mon mot de passe
                  </h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-slate-400">Mot de passe actuel</Label>
                      <div className="relative mt-1">
                        <Input
                          type={showPasswords ? 'text' : 'password'}
                          value={passwordForm.current}
                          onChange={(e) => setPasswordForm(p => ({ ...p, current: e.target.value }))}
                          placeholder="Mot de passe actuel"
                          className="bg-slate-800 border-slate-700 text-white pr-10"
                          data-testid="current-password-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPasswords(!showPasswords)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                        >
                          {showPasswords ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <div>
                      <Label className="text-slate-400">Nouveau mot de passe</Label>
                      <Input
                        type={showPasswords ? 'text' : 'password'}
                        value={passwordForm.new}
                        onChange={(e) => setPasswordForm(p => ({ ...p, new: e.target.value }))}
                        placeholder="Min. 8 caractères"
                        className="mt-1 bg-slate-800 border-slate-700 text-white"
                        data-testid="new-password-input"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-400">Confirmer le mot de passe</Label>
                      <Input
                        type={showPasswords ? 'text' : 'password'}
                        value={passwordForm.confirm}
                        onChange={(e) => setPasswordForm(p => ({ ...p, confirm: e.target.value }))}
                        placeholder="Confirmer"
                        className="mt-1 bg-slate-800 border-slate-700 text-white"
                        data-testid="confirm-password-input"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end mt-4">
                    <Button
                      onClick={handleChangePassword}
                      disabled={passwordSaving}
                      className="bg-slate-700 hover:bg-slate-600 text-white"
                      data-testid="change-password-btn"
                    >
                      {passwordSaving ? (
                        <><div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />Modification...</>
                      ) : (
                        <><Lock className="w-4 h-4 mr-2" />Modifier le mot de passe</>
                      )}
                    </Button>
                  </div>
                </div>

                {/* Save Button */}
                <div className="flex justify-end pt-4 border-t border-slate-800">
                  <Button
                    onClick={saveProfile}
                    disabled={profileSaving}
                    className="bg-[#DFFF00] hover:bg-[#c8e600] text-black font-semibold px-6"
                  >
                    {profileSaving ? (
                      <>
                        <div className="animate-spin w-4 h-4 border-2 border-black border-t-transparent rounded-full mr-2"></div>
                        Enregistrement...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Enregistrer
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Locations Tab */}
        {activeTab === 'locations' && (
          <>
        {reservations.length === 0 ? (
          <div className="bg-slate-900 rounded-2xl p-12 text-center border border-slate-800">
            <Package className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">Aucune location</h2>
            <p className="text-slate-400 mb-6">Vous n'avez pas encore de location en cours</p>
            <Button
              onClick={() => navigate('/reservation')}
              className="bg-[#DFFF00] hover:bg-[#c8e600] text-black font-semibold px-6 py-3 rounded-full"
            >
              Réserver un pack
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            {reservations.map((reservation) => {
              const returnRequest = getReturnRequestForReservation(reservation.id);
              const contractDate = reservation.contract_accepted_at ? new Date(reservation.contract_accepted_at) : null;
              
              // Calculate next anniversary date (1 month from delivery, rolling monthly)
              const startDate = reservation.delivered_at ? new Date(reservation.delivered_at)
                : reservation.rental_start_date ? new Date(reservation.rental_start_date)
                : contractDate 
                || (reservation.confirmed_at ? new Date(reservation.confirmed_at) : null)
                || (reservation.created_at ? new Date(reservation.created_at) : null);
              
              let nextAnniversary = null;
              if (startDate) {
                const now = new Date();
                const day = startDate.getDate();
                // Find the next anniversary: same day of month, in the future
                let candidate = new Date(now.getFullYear(), now.getMonth(), day);
                if (candidate <= now) {
                  candidate = new Date(now.getFullYear(), now.getMonth() + 1, day);
                }
                // Handle months with fewer days (e.g., day 31 in a 30-day month)
                if (candidate.getDate() !== day) {
                  candidate = new Date(candidate.getFullYear(), candidate.getMonth(), 0); // last day of previous month
                }
                nextAnniversary = candidate;
              }
              
              return (
              <div 
                key={reservation.id} 
                className="bg-slate-900 rounded-2xl overflow-hidden border border-slate-800"
                data-testid={`reservation-${reservation.id}`}
              >
                {/* Header with Pack Info and Status */}
                <div className="bg-slate-800/50 px-6 py-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Package className="w-5 h-5 text-[#DFFF00]" />
                    <span className="font-semibold text-white"><PackName name={reservation.pack_name} /></span>
                    <span className="text-slate-400">({reservation.pack_surface})</span>
                  </div>
                  {getStatusBadge(reservation.status, returnRequest)}
                </div>

                {/* Quick Actions Bar - EN HAUT */}
                <div className="bg-slate-800/30 px-6 py-3 border-b border-slate-700/50">
                  <div className="flex flex-wrap items-center gap-3">
                    {/* Contrat */}
                    {reservation.contract_accepted_at && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => downloadContract(reservation.id)}
                        className="text-slate-300 border-slate-600 hover:bg-slate-700 hover:border-slate-500"
                        data-testid="download-contract-btn"
                      >
                        <FileText className="w-4 h-4 mr-2" />
                        Contrat
                      </Button>
                    )}
                    
                    {/* Factures - Opens custom invoices modal */}
                    {reservation.stripe_customer_id && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openInvoicesModal(reservation.stripe_customer_id)}
                        className="text-[#DFFF00] border-[#DFFF00]/30 hover:bg-[#DFFF00]/10 hover:border-[#DFFF00]/50"
                        data-testid="invoices-btn"
                      >
                        <Sparkles className="w-4 h-4 mr-1" />
                        <Receipt className="w-4 h-4 mr-2" />
                        Factures
                      </Button>
                    )}
                    
                    {/* État des lieux */}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => toast.info('L\'état des lieux sera disponible après la livraison')}
                      className="text-slate-300 border-slate-600 hover:bg-slate-700 hover:border-slate-500"
                      data-testid="inspection-btn"
                    >
                      <ClipboardCheck className="w-4 h-4 mr-2" />
                      État des lieux
                    </Button>

                    {/* Spacer */}
                    <div className="flex-1" />

                    {/* Résiliation - En évidence si pas déjà demandée */}
                    {!returnRequest && ['confirmed', 'shipped', 'delivered'].includes(reservation.status) && (
                      <Button
                        size="sm"
                        onClick={() => openReturnModal(reservation)}
                        className="bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30"
                        data-testid="request-return-btn"
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Demander une résiliation
                      </Button>
                    )}

                    {/* Annuler commande - Uniquement pour les commandes non expédiées */}
                    {['pending', 'pending_validation', 'confirmed'].includes(reservation.status) && (
                      <Button
                        size="sm"
                        onClick={() => setShowCancelConfirm(reservation.id)}
                        className="bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30"
                        data-testid={`cancel-order-btn-${reservation.id}`}
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Annuler commande
                      </Button>
                    )}

                    {/* Bordereau de retour si disponible */}
                    {returnRequest?.return_label_url && (
                      <Button
                        size="sm"
                        onClick={() => window.open(returnRequest.return_label_url, '_blank')}
                        className="bg-blue-500 hover:bg-blue-600 text-white"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Bordereau de retour
                      </Button>
                    )}
                  </div>
                </div>

                {/* Return Request Banner */}
                {returnRequest && (
                  <div className={`px-6 py-3 ${returnRequest.status === 'label_sent' ? 'bg-blue-500/10' : 'bg-orange-500/10'}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {returnRequest.status === 'label_sent' ? (
                          <>
                            <Truck className="w-4 h-4 text-blue-400" />
                            <span className="text-blue-300 text-sm">
                              Bordereau de retour disponible - N° suivi: <strong>{returnRequest.tracking_number}</strong>
                            </span>
                          </>
                        ) : (
                          <>
                            <Clock className="w-4 h-4 text-orange-400" />
                            <span className="text-orange-300 text-sm">
                              Résiliation prévue le {new Date(returnRequest.effective_date).toLocaleDateString('fr-FR')}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Content */}
                <div className="p-6">
                  <div className="grid md:grid-cols-4 gap-6">
                    {/* Subscription Info */}
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-slate-400 text-sm">
                        <RefreshCw className="w-4 h-4" />
                        Abonnement mensuel
                      </div>
                      <div className="text-2xl font-bold text-white">
                        {reservation.monthly_price}€<span className="text-sm font-normal text-slate-400">/mois</span>
                      </div>
                      <div className="text-sm text-slate-500">
                        Prélèvement automatique
                      </div>
                    </div>

                    {/* Deposit Info */}
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-slate-400 text-sm">
                        <CreditCard className="w-4 h-4" />
                        Caution
                      </div>
                      <div className="text-2xl font-bold text-white">
                        {reservation.caution}€
                      </div>
                      <div className="text-sm text-slate-500">
                        {returnRequest ? 'Sera restituée après contrôle' : 'Restituable à la fin'}
                      </div>
                    </div>

                    {/* Anniversary / Next Billing Date */}
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-slate-400 text-sm">
                        <Calendar className="w-4 h-4" />
                        Date anniversaire
                      </div>
                      <div className="text-white">
                        {nextAnniversary ? (
                          <>
                            <span className="text-lg font-semibold">
                              {nextAnniversary.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                            </span>
                            <p className="text-sm text-slate-500 mt-1">
                              Renouvellement le {startDate.getDate()} de chaque mois
                            </p>
                            <p className="text-xs text-slate-600 mt-0.5">
                              Sans engagement — résiliable à tout moment
                            </p>
                          </>
                        ) : (
                          <span className="text-yellow-400">En attente de confirmation</span>
                        )}
                      </div>
                    </div>

                    {/* Status */}
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-slate-400 text-sm">
                        <CheckCircle className="w-4 h-4" />
                        Statut
                      </div>
                      <div className="text-white">
                        {reservation.status === 'delivered' ? (
                          <span className="text-emerald-400 font-semibold flex items-center gap-2">
                            <CheckCircle className="w-5 h-5" />
                            Livré - Location active
                          </span>
                        ) : reservation.status === 'shipped' ? (
                          <span className="text-blue-400 font-semibold flex items-center gap-2">
                            <Truck className="w-5 h-5" />
                            En cours de livraison
                          </span>
                        ) : reservation.status === 'confirmed' ? (
                          <span className="text-green-400 font-semibold flex items-center gap-2">
                            <CheckCircle className="w-5 h-5" />
                            Confirmée - En préparation
                          </span>
                        ) : reservation.status === 'pending_validation' ? (
                          <span className="text-yellow-400 font-semibold flex items-center gap-2">
                            <Clock className="w-5 h-5" />
                            En validation
                          </span>
                        ) : returnRequest ? (
                          <span className="text-orange-400 font-semibold flex items-center gap-2">
                            <XCircle className="w-5 h-5" />
                            Résiliation en cours
                          </span>
                        ) : (
                          <span className="text-slate-400">En attente</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Footer with reservation date */}
                  <div className="mt-6 pt-4 border-t border-slate-800 text-sm text-slate-500">
                    Réservation effectuée le {new Date(reservation.created_at).toLocaleDateString('fr-FR')}
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        )}
          </>
        )}
      </main>

      {/* Cancel Order Confirmation Modal */}
      {showCancelConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl p-6 max-w-md w-full mx-4" data-testid="cancel-confirm-modal">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-red-400" />
              </div>
              <h3 className="text-lg font-semibold text-white">Annuler cette commande ?</h3>
            </div>
            <p className="text-slate-400 text-sm mb-6">
              Cette action est irréversible. Votre commande sera annulée et un remboursement automatique sera effectué sur votre moyen de paiement.
            </p>
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowCancelConfirm(null)}
                className="border-slate-600 text-slate-300"
                data-testid="cancel-confirm-no"
              >
                Non, garder ma commande
              </Button>
              <Button
                size="sm"
                onClick={() => handleCancelOrder(showCancelConfirm)}
                disabled={cancellingId === showCancelConfirm}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="cancel-confirm-yes"
              >
                {cancellingId === showCancelConfirm ? (
                  <><div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />Annulation...</>
                ) : (
                  'Oui, annuler et rembourser'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Return Request Modal */}
      <ReturnRequestModal
        isOpen={showReturnModal}
        onClose={() => setShowReturnModal(false)}
        reservation={selectedReservation}
        onSuccess={() => { if (user?.email) fetchReservations(user.email); }}
      />

      {/* Invoices Modal */}
      <InvoicesModal
        isOpen={showInvoicesModal}
        onClose={() => setShowInvoicesModal(false)}
        customerId={selectedCustomerId}
        customerName={user?.name || 'Client'}
      />
    </div>
  );
};

export default ClientDashboard;
